import { GameState, Card, Player } from '../types/game';
import { shuffleDeck } from '../data/cards';

/**
 * Card Effects Engine
 *
 * Pure-function system for executing fieldcraft card effects.
 * Returns new game state + optional pending actions that require UI (card picker).
 */

/**
 * Route a played card to either the discard pile or the burn pile based on
 * its isBurn flag. Returns the new hand/discard/burnPile slices so callers
 * can spread them into their player state updates like so:
 *
 *     player: { ...prev.player, ...routePlayedCard(prev.player, card), ... }
 *
 * This centralizes a previously-duplicated pattern (filter out of hand,
 * push to discard) that was ignoring isBurn at 20+ call sites in
 * GameScreen.tsx. The bug manifested most obviously with Double Tap copies,
 * which are flagged isBurn:true but were landing in discard instead of the
 * burn pile — the copy would then shuffle back into the deck as a
 * permanent 0 AP version of the card.
 */
export function routePlayedCard(player: Player, card: Card): {
  hand: Card[];
  discard: Card[];
  burnPile: Card[];
  prepBonus: number;
  prepBuiltThisTurn: number;
} {
  const hand = player.hand.filter(c => c.id !== card.id);
  // Sharpen: +10% prep per card played while active (including the card
  // that activates Sharpen — the bump in the Sharpen branch stacks with
  // this one so playing Sharpen itself is worth +20% on its own turn).
  // Flat +10% per play, not multiplicative/compounding.
  const sharpenBump = player.sharpenActive ? 0.1 : 0;
  const prepBonus = player.prepBonus + sharpenBump;
  const prepBuiltThisTurn = player.prepBuiltThisTurn + sharpenBump;
  if (card.isBurn) {
    return {
      hand,
      discard: player.discard,
      burnPile: [...player.burnPile, card],
      prepBonus,
      prepBuiltThisTurn,
    };
  }
  return {
    hand,
    discard: [...player.discard, card],
    burnPile: player.burnPile,
    prepBonus,
    prepBuiltThisTurn,
  };
}

// --- Pending actions that require UI interaction ---

export type PendingCardAction =
  | { type: 'chain-discard'; cardPlayed: Card }
  | { type: 'adapt-discard' }
  | { type: 'copy-pick' }
  | { type: 'recall-pick' }
  | { type: 'stash-pick' }
  | { type: 'field-modify-pick' }
  | { type: 'overclock-pick' }
  // Double Tap now stores the card that triggered it so resolveDoubleTapPick
  // can consume it AT PICK TIME instead of at play time. This lets the
  // cancel flow leave the card in the player's hand untouched — the player
  // only "commits" the Double Tap play once they actually pick a target.
  | { type: 'double-tap-pick'; cardPlayed: Card }
  | { type: 'improvise-pick'; topCards: Card[] };

export interface CardEffectResult {
  newState: GameState;
  pendingAction?: PendingCardAction;
  notification?: string;
}

/** Card names handled by the fieldcraft effect engine */
const FIELDCRAFT_CARDS = new Set([
  'Adrenaline', 'Detour', 'Calculated',
  'Adapt', 'Copy', 'Recall', 'Stash',
  'Field Modify', 'Overclock', 'Sharpen',
  'Patience', 'Opportunist', 'Operational Integrity',
  'Improvise',
  'Double Tap', 'Contingency',
]);

export function isFieldcraftEffectCard(card: Card): boolean {
  return FIELDCRAFT_CARDS.has(card.name);
}

/**
 * Common pre-play: deduct AP, remove card from hand, route to discard or burn pile.
 * Also increments cardsPlayedThisTurn and handles Double Tap duplication.
 */
function prePlay(state: GameState, card: Card): GameState {
  const player = { ...state.player };
  player.ap = player.ap - card.apCost;
  player.hand = player.hand.filter(c => c.id !== card.id);
  player.cardsPlayedThisTurn = player.cardsPlayedThisTurn + 1;
  player.lastPlayedMovement = false; // fieldcraft cards are not movement

  if (card.isBurn) {
    player.burnPile = [...player.burnPile, card];
  } else {
    player.discard = [...player.discard, card];
  }

  // Sharpen: if active, gain +10% prep per card played
  if (player.sharpenActive) {
    player.prepBonus = player.prepBonus + 0.1;
    player.prepBuiltThisTurn = player.prepBuiltThisTurn + 0.1;
  }

  return { ...state, player };
}

/**
 * Execute a fieldcraft card effect.
 * Returns updated state + optional pending action for UI picker.
 */
export function executeFieldcraftEffect(state: GameState, card: Card): CardEffectResult {
  let s = prePlay(state, card);
  const p = s.player;

  switch (card.name) {
    // --- AP Manipulation ---

    case 'Adrenaline': {
      return {
        newState: { ...s, player: { ...p, ap: p.ap + 1, adrenalineApThisTurn: p.adrenalineApThisTurn + 1 } },
      };
    }

    case 'Detour': {
      // +1 AP, then Chain (discard 1, draw 1) handled via pending action
      return {
        newState: { ...s, player: { ...p, ap: p.ap + 1 } },
        pendingAction: { type: 'chain-discard', cardPlayed: card },
        notification: 'Detour: +1 AP. Choose a card to discard (Chain).',
      };
    }

    case 'Calculated': {
      return {
        newState: { ...s, player: { ...p, bonusApNextTurn: p.bonusApNextTurn + 2 } },
        notification: 'Calculated: +2 AP next turn.',
      };
    }

    // --- Card Manipulation ---

    case 'Adapt': {
      if (p.hand.length === 0) {
        // No cards to discard — just draw 3 (reshuffle discard if needed)
        let deck = [...p.deck];
        let discard = [...p.discard];
        if (deck.length < 3 && discard.length > 0) {
          deck = shuffleDeck([...deck, ...discard]);
          discard = [];
        }
        const draw3 = deck.slice(0, 3);
        const remainDeck = deck.slice(3);
        return {
          newState: { ...s, player: { ...p, hand: [...p.hand, ...draw3], deck: remainDeck, discard } },
          notification: `Adapt: Drew ${draw3.length} cards.`,
        };
      }
      return {
        newState: s,
        pendingAction: { type: 'adapt-discard' },
        notification: 'Adapt: Choose a card to discard, then draw 3.',
      };
    }

    case 'Copy': {
      if (p.hand.length === 0) {
        return { newState: s, notification: 'Copy: No cards in hand to copy.' };
      }
      return {
        newState: s,
        pendingAction: { type: 'copy-pick' },
        notification: 'Copy: Choose a card to duplicate into your discard pile.',
      };
    }

    case 'Recall': {
      if (p.discard.length === 0) {
        return { newState: s, notification: 'Recall: Discard pile is empty.' };
      }
      return {
        newState: s,
        pendingAction: { type: 'recall-pick' },
        notification: 'Recall: Choose a card from your discard pile.',
      };
    }

    case 'Stash': {
      if (p.hand.length === 0) {
        return { newState: s, notification: 'Stash: No cards in hand to retain.' };
      }
      return {
        newState: s,
        pendingAction: { type: 'stash-pick' },
        notification: 'Stash: Choose a card to retain in your hand next turn.',
      };
    }

    // --- Card Upgrading ---

    case 'Field Modify': {
      if (p.hand.length === 0) {
        return { newState: s, notification: 'Field Modify: No cards in hand to modify.' };
      }
      return {
        newState: s,
        pendingAction: { type: 'field-modify-pick' },
        notification: 'Field Modify: Choose a card to reduce its AP cost by 1.',
      };
    }

    case 'Overclock': {
      const movementCards = p.hand.filter(c => !!c.movementRange);
      if (movementCards.length === 0) {
        return { newState: s, notification: 'Overclock: No movement cards in hand.' };
      }
      return {
        newState: s,
        pendingAction: { type: 'overclock-pick' },
        notification: 'Overclock: Choose a movement card to double its range.',
      };
    }

    case 'Sharpen': {
      // Already played (counted in cardsPlayedThisTurn), set flag
      // Sharpen itself gives +10% via the sharpenActive check in prePlay for subsequent cards
      // But Sharpen counts itself too — so grant immediate +10%
      return {
        newState: { ...s, player: { ...p, sharpenActive: true, prepBonus: p.prepBonus + 0.1, prepBuiltThisTurn: p.prepBuiltThisTurn + 0.1 } },
        notification: `Sharpen: +10% Prep per card played this turn. Current Prep: ${Math.round(Math.min(1, p.prepBonus + 0.1) * 100)}%`,
      };
    }

    // --- Conditional / Synergy ---

    case 'Patience': {
      // Patience: permanently +1 max AP for the rest of the round.
      // Can only be played if no other card was played this turn (checked in GameScreen).
      return {
        newState: {
          ...s,
          player: {
            ...p,
            maxAp: p.maxAp + 1,
            patienceActive: true,
          },
        },
        notification: 'Patience: +1 max AP for the rest of this round.',
      };
    }

    case 'Opportunist': {
      const draw2 = p.deck.slice(0, 2);
      const remainDeck = p.deck.slice(2);
      return {
        newState: { ...s, player: { ...p, hand: [...p.hand, ...draw2], deck: remainDeck } },
        notification: `Opportunist: Drew ${draw2.length} card${draw2.length !== 1 ? 's' : ''}.`,
      };
    }

    case 'Operational Integrity': {
      return {
        newState: { ...s, player: { ...p, operationalIntegrityActive: true } },
        notification: 'Operational Integrity: Next turn — +2 AP and +3 additional cards drawn.',
      };
    }

    // --- Deck Manipulation ---

    case 'Improvise': {
      const topCards = p.deck.slice(0, 3);
      if (topCards.length === 0) {
        return { newState: s, notification: 'Improvise: Deck is empty.' };
      }
      return {
        newState: s,
        pendingAction: { type: 'improvise-pick', topCards },
        notification: `Improvise: Pick 1 of ${topCards.length} cards to add to your hand.`,
      };
    }

    // --- Combo Enablers ---

    case 'Double Tap': {
      // Special case: Double Tap does NOT go through prePlay here. We want
      // the card to stay in the player's hand (with AP un-spent) until the
      // player actually picks a target in the picker. Canceling the picker
      // then leaves the game in its pre-play state — no refund gymnastics
      // needed. resolveDoubleTapPick calls prePlay() on the stored
      // cardPlayed at pick time to commit the play.
      //
      // We need to check the pre-prePlay hand for emptiness (not `p` which
      // is post-prePlay). Fetch the original state's player hand to count.
      // hand length check uses `state.player.hand` minus the DT card itself
      // since the DT card shouldn't count as a target.
      const originalHand = state.player.hand.filter(c => c.id !== card.id);
      if (originalHand.length === 0) {
        return { newState: state, notification: 'Double Tap: No cards in hand to duplicate.' };
      }
      return {
        // Return the ORIGINAL state (not s = prePlay(state)) so the DT
        // card stays in hand and AP is not deducted. prePlay runs later
        // in resolveDoubleTapPick once the player commits.
        newState: state,
        pendingAction: { type: 'double-tap-pick', cardPlayed: card },
        notification: 'Double Tap: Choose a card in your hand to duplicate.',
      };
    }

    case 'Contingency': {
      const newCount = p.contingencyActive + 1;
      return {
        newState: { ...s, player: { ...p, contingencyActive: newCount } },
        notification: newCount > 1
          ? `Contingency ×${newCount}: The next ${newCount} Tactics cards you draw cost 0 AP.`
          : 'Contingency: The next Tactics card you draw costs 0 AP.',
      };
    }

    default:
      return { newState: s };
  }
}

/**
 * Handle Double Tap: if active and a non-fieldcraft-engine card is about to be played,
 * create a burn-copy in hand. Call this BEFORE executing the card's normal effect.
 * Returns the updated player (with copy in hand) or null if Double Tap wasn't active.
 */
/**
 * Resolve Double Tap pick: commit the Double Tap card via prePlay (which
 * deducts AP, removes the DT card from hand, and routes it to discard)
 * THEN create a 0-AP burn-copy of the picked target card and add it to
 * hand. Called from the onPick handler of the double-tap-pick picker.
 *
 * We defer the prePlay until this point so cancelling the picker leaves
 * the player's state untouched — the Double Tap card stays in hand, AP
 * isn't deducted, and the player can try again.
 *
 * @param state       Current game state (Double Tap still in hand, AP untouched)
 * @param pickedCard  The card the player chose to duplicate (a different card in hand)
 * @param cardPlayed  The original Double Tap card (from pendingAction.cardPlayed)
 */
export function resolveDoubleTapPick(
  state: GameState,
  pickedCard: Card,
  cardPlayed: Card,
): CardEffectResult {
  // Commit the Double Tap play now — deducts AP, removes DT card from
  // hand, routes to discard/burn pile, bumps cardsPlayedThisTurn, fires
  // Sharpen if active. Same prePlay every other fieldcraft card uses.
  const committed = prePlay(state, cardPlayed);
  const p = committed.player;

  const copy: Card = {
    ...pickedCard,
    id: `dt-copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    originalApCost: pickedCard.apCost,
    apCost: 0,
    isBurn: true,        // burns when played (prePlay routes it to burnPile)
    isEphemeral: true,   // AND also burns at end of turn if not played —
                         // a DT copy must never outlive the turn it was created on
  };
  return {
    newState: { ...committed, player: { ...p, hand: [...p.hand, copy] } },
    notification: `Double Tap: ${pickedCard.name} duplicated (0 AP, burns at end of turn).`,
  };
}

/**
 * LEGACY — kept for backward compatibility but no longer used.
 * Double Tap now uses a picker (resolveDoubleTapPick) instead of a passive buff.
 */
export function applyDoubleTap(player: Player, cardBeingPlayed: Card): Player | null {
  if (!player.doubleTapActive) return null;
  if (isFieldcraftEffectCard(cardBeingPlayed)) return null;
  if (cardBeingPlayed.id.startsWith('scavenge-') || cardBeingPlayed.id.startsWith('extract-')) return null;
  const copy: Card = {
    ...cardBeingPlayed,
    id: `dt-copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    originalApCost: cardBeingPlayed.apCost,
    apCost: 0,
    isBurn: true,        // burns when played (prePlay routes it to burnPile)
    isEphemeral: true,   // AND also burns at end of turn if not played
  };
  return {
    ...player,
    doubleTapActive: false,
    hand: [...player.hand, copy],
  };
}

// --- Resolve pending actions after UI picker selection ---

export function resolveChainDiscard(state: GameState, discardedCard: Card): CardEffectResult {
  const p = state.player;
  const newHand = p.hand.filter(c => c.id !== discardedCard.id);
  const newDiscard = [...p.discard, discardedCard];
  // Draw 1
  const drawn = p.deck.slice(0, 1);
  const remainDeck = p.deck.slice(1);
  return {
    newState: {
      ...state,
      player: { ...p, hand: [...newHand, ...drawn], discard: newDiscard, deck: remainDeck },
    },
    notification: drawn.length > 0 ? `Chain: Discarded ${discardedCard.name}, drew 1 card.` : `Chain: Discarded ${discardedCard.name}, deck empty.`,
  };
}

export function resolveAdaptDiscard(state: GameState, discardedCard: Card): CardEffectResult {
  const p = state.player;
  const newHand = p.hand.filter(c => c.id !== discardedCard.id);
  let deck = [...p.deck];
  let discard = [...p.discard, discardedCard];
  // Reshuffle if deck can't supply 3 cards
  if (deck.length < 3 && discard.length > 0) {
    deck = shuffleDeck([...deck, ...discard]);
    discard = [];
  }
  const draw3 = deck.slice(0, 3);
  const remainDeck = deck.slice(3);
  return {
    newState: {
      ...state,
      player: { ...p, hand: [...newHand, ...draw3], discard, deck: remainDeck },
    },
    notification: `Adapt: Discarded ${discardedCard.name}, drew ${draw3.length} cards.`,
  };
}

export function resolveCopyPick(state: GameState, copiedCard: Card): CardEffectResult {
  const p = state.player;
  // Create a duplicate and add to discard pile
  const copy: Card = {
    ...copiedCard,
    id: `copy-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  };
  return {
    newState: { ...state, player: { ...p, discard: [...p.discard, copy] } },
    notification: `Copy: Duplicated ${copiedCard.name} into discard pile.`,
  };
}

export function resolveRecallPick(state: GameState, recalledCard: Card): CardEffectResult {
  const p = state.player;
  return {
    newState: {
      ...state,
      player: {
        ...p,
        discard: p.discard.filter(c => c.id !== recalledCard.id),
        hand: [...p.hand, recalledCard],
      },
    },
    notification: `Recall: Added ${recalledCard.name} to hand.`,
  };
}

export function resolveStashPick(state: GameState, stashedCard: Card): CardEffectResult {
  const p = state.player;
  return {
    newState: {
      ...state,
      player: { ...p, retainedCardIds: [...p.retainedCardIds, stashedCard.id] },
    },
    notification: `Stash: ${stashedCard.name} will be retained at end of turn.`,
  };
}

export function resolveFieldModifyPick(state: GameState, targetCard: Card): CardEffectResult {
  const p = state.player;
  // Mutate the card in all piles — find it by ID
  const mutate = (cards: Card[]) => cards.map(c => {
    if (c.id === targetCard.id) {
      return {
        ...c,
        originalApCost: c.originalApCost ?? c.apCost,
        apCost: Math.max(0, c.apCost - 1),
      };
    }
    return c;
  });
  return {
    newState: {
      ...state,
      player: { ...p, hand: mutate(p.hand), deck: mutate(p.deck), discard: mutate(p.discard) },
    },
    notification: `Field Modify: ${targetCard.name} now costs ${Math.max(0, targetCard.apCost - 1)} AP (was ${targetCard.apCost}).`,
  };
}

export function resolveOverclockPick(state: GameState, targetCard: Card): CardEffectResult {
  const p = state.player;
  const mutate = (cards: Card[]) => cards.map(c => {
    if (c.id === targetCard.id && c.movementRange && !c.isOverclocked) {
      return { ...c, movementRange: c.movementRange * 2, isOverclocked: true };
    }
    return c;
  });
  return {
    newState: {
      ...state,
      player: { ...p, hand: mutate(p.hand), deck: mutate(p.deck), discard: mutate(p.discard) },
    },
    notification: `Overclock: ${targetCard.name} range doubled to ${(targetCard.movementRange ?? 0) * 2}m.`,
  };
}

export function resolveImprovisePick(state: GameState, chosenCard: Card, topCards: Card[]): CardEffectResult {
  const p = state.player;
  // Remove top N cards from deck, add chosen to hand, put rest back on top
  const remaining = topCards.filter(c => c.id !== chosenCard.id);
  const newDeck = [...remaining, ...p.deck.slice(topCards.length)];
  return {
    newState: {
      ...state,
      player: { ...p, hand: [...p.hand, chosenCard], deck: newDeck },
    },
    notification: `Improvise: Added ${chosenCard.name} to hand.`,
  };
}
